/**
 * State for the Admin Console's audit history (DEV-5969).
 *
 * Owns the filters, the page and the loaded page of events. Nothing here
 * writes: the trail is append-only and the endpoint is `GET` only.
 *
 * A 400 (an unusable filter — malformed user id, unknown action, a range whose
 * start is after its end) is surfaced as `filterError` next to the filters, so
 * the admin sees why the page did not change instead of a stale or empty list.
 * Any other failure is a load error with a retry, like the user list.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

import {
  AUDIT_DEFAULT_PAGE_SIZE,
  AuditEvent,
  AuditEventFilters,
  getAdminAuditEvents,
} from "Utils/adminAuditAPI";
import { showNetworkErrorToast } from "Utils/api";

export interface AdminAuditEventsState {
  events: AuditEvent[];
  count: number;
  page: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
  filters: AuditEventFilters;
  isLoading: boolean;
  isLoadingError: boolean;
  filterError: string | null;
  reload: () => Promise<void>;
  applyFilters: (filters: AuditEventFilters) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
}

/**
 * Flattens a DRF validation body (`{"occurredAfter": ["..."], "nonFieldErrors":
 * ["..."]}` or `{"detail": "..."}`) into one readable line.
 */
export const describeFilterError = (data: unknown): string => {
  if (typeof data === "string" && data.trim()) return data;
  if (data && typeof data === "object") {
    const parts = Object.entries(data as Record<string, unknown>).map(
      ([key, value]) => {
        const messages = Array.isArray(value) ? value : [value];
        const text = messages.map((message) => String(message)).join(" ");
        return key === "detail" || key === "nonFieldErrors"
          ? text
          : `${key}: ${text}`;
      }
    );
    if (parts.length) return parts.join(" ");
  }
  return "The filters were rejected by the server.";
};

interface LoadedPage {
  events: AuditEvent[];
  count: number;
  page: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

const EMPTY_PAGE: LoadedPage = {
  events: [],
  count: 0,
  page: 1,
  pageSize: AUDIT_DEFAULT_PAGE_SIZE,
  hasNext: false,
  hasPrevious: false,
};

export const useAdminAuditEvents = (): AdminAuditEventsState => {
  // The query being requested and the page last loaded successfully are kept
  // apart: on a rejected filter the loaded page stays on screen, so its label
  // and controls must keep describing it, not the request that failed.
  const [page, setPageState] = useState<number>(1);
  const [pageSize, setPageSizeState] = useState<number>(
    AUDIT_DEFAULT_PAGE_SIZE
  );
  const [filters, setFilters] = useState<AuditEventFilters>({});
  const [loaded, setLoaded] = useState<LoadedPage>(EMPTY_PAGE);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingError, setIsLoadingError] = useState<boolean>(false);
  const [filterError, setFilterError] = useState<string | null>(null);
  const requestSeq = useRef(0);

  const reload = useCallback(async () => {
    const requestId = ++requestSeq.current;
    const isCurrent = () => requestId === requestSeq.current;
    setIsLoading(true);
    setIsLoadingError(false);
    setFilterError(null);
    try {
      const result = await getAdminAuditEvents({ ...filters, page, pageSize });
      if (!isCurrent()) return;
      setLoaded({
        events: result.results,
        count: result.count,
        page,
        pageSize,
        hasNext: result.next !== null,
        hasPrevious: result.previous !== null,
      });
    } catch (error) {
      if (!isCurrent()) return;
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        // The server refused the filters: keep the previous page on screen and
        // say why, rather than toasting a generic "request failed".
        setFilterError(describeFilterError(error.response.data));
      } else {
        setIsLoadingError(true);
        showNetworkErrorToast(error);
      }
    } finally {
      if (isCurrent()) setIsLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    reload();
  }, [reload]);

  const applyFilters = useCallback((next: AuditEventFilters) => {
    setFilters(next);
    setPageState(1);
  }, []);

  const setPage = useCallback((next: number) => {
    setPageState(Math.max(1, next));
  }, []);

  const setPageSize = useCallback((next: number) => {
    setPageSizeState(next);
    setPageState(1);
  }, []);

  return {
    events: loaded.events,
    count: loaded.count,
    page: loaded.page,
    pageSize: loaded.pageSize,
    hasNext: loaded.hasNext,
    hasPrevious: loaded.hasPrevious,
    filters,
    isLoading,
    isLoadingError,
    filterError,
    reload,
    applyFilters,
    setPage,
    setPageSize,
  };
};
